import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  Monitor, 
  Coffee, 
  Wifi, 
  Users,
  Briefcase,
  Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Konference = () => {
  return (
    <div className="pt-0">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/Kundebilleder/imgi_18_Auditorum.1-1536x1152.jpg" 
            className="w-full h-full object-cover" 
            alt="Konference på GuldBoSund Konference & Event" 
          />
          <div className="absolute inset-0 bg-[#081f32]/60" />
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mt-20">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2 }}
            className="text-5xl md:text-7xl font-light mb-6 uppercase tracking-wider"
          >
            Konference & Event
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="text-xl md:text-2xl font-light"
          >
            Professionelle rammer, der inspirerer til resultater
          </motion.p>
        </div>
      </section>

      {/* Intro Stats */}
      <section className="bg-[#081f32] py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Users, label: "Op til 350", sub: "Gæster i auditoriet" },
              { icon: Zap, label: "Moderne AV", sub: "Altid opdateret udstyr" },
              { icon: Wifi, label: "Lynhurtigt", sub: "Fiber-internet overalt" },
              { icon: Coffee, label: "Gastronomi", sub: "Forplejning i topklasse" }
            ].map((stat, i) => (
              <div key={i} className="text-center text-white">
                <stat.icon className="mx-auto mb-3 text-[#dab256]" size={24} />
                <p className="text-xl font-light mb-1">{stat.label}</p>
                <p className="text-[10px] text-white/50 uppercase tracking-widest">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-light text-[#081f32] mb-8 uppercase tracking-wider leading-tight">Giv dit team de bedste forudsætninger</h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed font-light">
                <p>
                  Hos GuldBoSund Konference & Event ved vi, at omgivelserne betyder alt for kreativiteten og produktiviteten. Vores moderne auditorium og fleksible mødelokaler er designet til at understøtte både strategiske møder og store konferencer.
                </p>
                <p>
                  Med roen fra Guldborgsund lige uden for vinduerne er der plads til at tænke store tanker. Vi sørger for alt det tekniske og den kulinariske forkælelse, så I kan fokusere på indholdet.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img src="/Kundebilleder/imgi_19_Auditorum.1-2048x1536.jpg" className="rounded-3xl shadow-2xl" alt="Auditorium" />
            </motion.div>
          </div>

          {/* Pricing Packages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Dagsmøde */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-stone-50 p-10 rounded-3xl border border-stone-200"
            >
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="text-2xl font-light text-[#081f32] uppercase tracking-wider mb-2">Dagsmøde</h3>
                  <p className="text-gray-500 font-light">Kl. 8.00 – 16.00</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-light text-[#081f32]">585,-</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest">pr. person ex moms</p>
                </div>
              </div>
              <ul className="space-y-4 mb-10">
                {[
                  "Lokaleleje inkl. standard AV-udstyr",
                  "Morgenbuffet med lokale specialiteter",
                  "Lækker frokostbuffet inkl. én vand",
                  "Eftermiddagskaffe med hjemmebag",
                  "Is vand og frisk frugt hele dagen"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#dab256] mt-1" />
                    <span className="text-gray-700 font-light">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/kontakt" className="block w-full border-2 border-[#081f32] text-[#081f32] py-4 rounded-xl text-center text-xs font-light uppercase tracking-widest hover:bg-[#081f32] hover:text-white transition-all">
                Book dagsmøde
              </Link>
            </motion.div>

            {/* Heldagsmøde */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-[#081f32] p-10 rounded-3xl text-white shadow-xl"
            >
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="text-2xl font-light uppercase tracking-wider mb-2">Heldagsmøde</h3>
                  <p className="text-stone-400 font-light">Kl. 8.00 – 21.00</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-light text-[#dab256]">895,-</p>
                  <p className="text-[10px] text-stone-400 uppercase tracking-widest">pr. person ex moms</p>
                </div>
              </div>
              <ul className="space-y-4 mb-10 text-stone-300">
                {[
                  "Alt fra dagsmødet inkluderet",
                  "Aftenmenu sammensat af kokken",
                  "Kaffe/te og sødt til aftenen",
                  "Lokaleleje frem til kl. 21.00",
                  "Moderne AV-udstyr og teknisk assistance"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#dab256] mt-1" />
                    <span className="font-light">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/kontakt" className="block w-full bg-[#dab256] text-[#081f32] py-4 rounded-xl text-center text-xs font-light uppercase tracking-widest hover:bg-[#c4a04a] transition-all">
                Få et skræddersyet tilbud
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Konference;
